import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronLeft,
  KeyRound,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const ONBOARDING_STORAGE_KEY = 'asm_onboarding_complete';
const TOTAL_STEPS = 6;

function buildInstallerCommand() {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.amrsecureops.com';
  return `curl -fsSL "${origin}/api/v1/enrollment-tokens/install.sh?token=ORG-TOKEN" | bash -s -- EMPLOYEE_ID`;
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/70 px-4 py-3">
      <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-semibold text-white">{value}</p>
    </div>
  );
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(() => localStorage.getItem(ONBOARDING_STORAGE_KEY) !== 'true');
  const [stepIndex, setStepIndex] = useState(0);
  const [stats, setStats] = useState({ users: 1, devices: 0, policies: 0 });

  useEffect(() => {
    if (!open) return undefined;

    let active = true;

    const loadStats = async () => {
      const [usersResult, devicesResult, policiesResult] = await Promise.allSettled([
        api.get('/users', { params: { page: 1, page_size: 1 } }),
        api.get('/devices', { params: { page: 1, page_size: 1 } }),
        api.get('/policies', { params: { page: 1, page_size: 1 } }),
      ]);

      if (!active) return;

      setStats({
        users: usersResult.status === 'fulfilled' ? usersResult.value.data?.total || 1 : 1,
        devices: devicesResult.status === 'fulfilled' ? devicesResult.value.data?.total || 0 : 0,
        policies: policiesResult.status === 'fulfilled' ? policiesResult.value.data?.total || 0 : 0,
      });
    };

    loadStats();

    return () => {
      active = false;
    };
  }, [open]);

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setOpen(false);
  };

  const organizationName =
    user?.organization_name || user?.org_name || user?.company_name || user?.org_id || 'Main Operations';

  const steps = [
    {
      eyebrow: 'Welcome',
      title: 'Welcome to ASM SecureOps',
      icon: Shield,
      description:
        'Protect your organization from insider threats while respecting employee privacy.',
      content: (
        <div className="grid gap-4 md:grid-cols-3">
          <StatPill label="Plan" value="Enterprise demo" />
          <StatPill label="Workspace" value={organizationName} />
          <StatPill label="Admin" value={user?.email || 'owner@company.com'} />
        </div>
      ),
      action: (
        <div className="rounded-2xl border border-blue-500/20 bg-blue-500/10 p-4 text-sm text-blue-100">
          ASM brings telemetry, risk scoring, alerts, reports, and policy controls into one command
          center so security teams can act fast without drowning in raw activity data.
        </div>
      ),
    },
    {
      eyebrow: 'Organization',
      title: 'Set Up Your Organization',
      icon: Building2,
      description: 'Verify your workspace identity and admin ownership before adding more operators.',
      content: (
        <div className="grid gap-4 md:grid-cols-2">
          <StatPill label="Organization ID" value={user?.org_id || 'Not available'} />
          <StatPill label="Primary Admin" value={user?.email || 'Unknown'} />
        </div>
      ),
      action: (
        <Button
          className="bg-slate-100 text-slate-950 hover:bg-white"
          onClick={() => navigate('/users')}
          type="button"
        >
          Open Admin Settings
        </Button>
      ),
    },
    {
      eyebrow: 'Users',
      title: 'Invite Your Team',
      icon: Users,
      description: 'Add security admins and managers so alerts, approvals, and escalations are shared.',
      content: (
        <div className="grid gap-4 md:grid-cols-2">
          <StatPill label="Current users" value={stats.users} />
          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Next action</p>
            <p className="mt-2 text-base text-slate-200">
              Open the Users page and use the highlighted
              <span className="mx-2 rounded-full border border-blue-400/40 bg-blue-500/15 px-2 py-1 text-xs font-medium text-blue-100">
                Invite User
              </span>
              action.
            </p>
          </div>
        </div>
      ),
      action: (
        <Button
          className="bg-blue-600 text-white hover:bg-blue-500"
          onClick={() => navigate('/users')}
          type="button"
        >
          Go to Users
        </Button>
      ),
    },
    {
      eyebrow: 'Devices',
      title: 'Enroll Your First Device',
      icon: KeyRound,
      description: 'Deploy an agent to begin collecting activity, alerts, and risk telemetry.',
      content: (
        <div className="space-y-4">
          <StatPill label="Managed devices" value={stats.devices} />
          <div className="rounded-2xl border border-slate-800 bg-slate-950/90 p-4">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Installer preview</p>
            <code className="mt-3 block overflow-x-auto text-sm text-emerald-300">
              {buildInstallerCommand()}
            </code>
          </div>
        </div>
      ),
      action: (
        <Button
          className="bg-blue-600 text-white hover:bg-blue-500"
          onClick={() => navigate('/enrollment')}
          type="button"
        >
          Open Enrollment
        </Button>
      ),
    },
    {
      eyebrow: 'Policies',
      title: 'Configure Monitoring Policies',
      icon: Shield,
      description:
        'Policies control what gets monitored, which actions generate alerts, and how strictly risk signals are scored.',
      content: (
        <div className="grid gap-4 md:grid-cols-2">
          <StatPill label="Policies configured" value={stats.policies} />
          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4 text-sm text-slate-300">
            Tune web, file, USB, AI, and alerting rules before broad rollout so security and privacy
            expectations are explicit from day one.
          </div>
        </div>
      ),
      action: (
        <Button
          className="bg-blue-600 text-white hover:bg-blue-500"
          onClick={() => navigate('/policies')}
          type="button"
        >
          Review Policies
        </Button>
      ),
    },
    {
      eyebrow: 'Launch',
      title: "You're All Set!",
      icon: CheckCircle2,
      description: 'Your workspace is ready for live monitoring, alerts, and executive reporting.',
      content: (
        <div className="grid gap-4 md:grid-cols-3">
          <StatPill label="Users" value={stats.users} />
          <StatPill label="Devices" value={stats.devices} />
          <StatPill label="Policies" value={stats.policies} />
        </div>
      ),
      action: (
        <Button
          className="bg-emerald-500 text-slate-950 hover:bg-emerald-400"
          onClick={completeOnboarding}
          type="button"
        >
          Go to Dashboard
        </Button>
      ),
    },
  ];

  if (!open) {
    return null;
  }

  const currentStep = steps[stepIndex];
  const StepIcon = currentStep.icon;

  return (
    <div className="fixed inset-0 z-[70] flex min-h-screen items-center justify-center bg-slate-950/88 p-4 backdrop-blur-md">
      <div className="w-full max-w-5xl overflow-hidden rounded-[32px] border border-slate-800 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_35%),linear-gradient(180deg,_rgba(15,23,42,0.98),_rgba(2,6,23,1))] shadow-[0_30px_100px_rgba(2,6,23,0.8)] transition-all duration-300">
        <div className="border-b border-slate-800 px-6 py-5 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-500">
                {currentStep.eyebrow}
              </p>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-blue-400/30 bg-blue-500/10 text-blue-200">
                  <StepIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-3xl font-semibold tracking-tight text-white">
                    {currentStep.title}
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm text-slate-400">
                    {currentStep.description}
                  </p>
                </div>
              </div>
            </div>

            <button
              className="self-start text-sm text-slate-500 transition hover:text-slate-200"
              onClick={completeOnboarding}
              type="button"
            >
              Skip
            </button>
          </div>

          <div className="mt-5 space-y-2">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-slate-500">
              <span>
                {stepIndex + 1}/{TOTAL_STEPS}
              </span>
              <span>Setup progress</span>
            </div>
            <Progress className="h-2 bg-slate-800 [&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-emerald-400" value={((stepIndex + 1) / TOTAL_STEPS) * 100} />
          </div>
        </div>

        <div className="grid gap-6 px-6 py-6 sm:px-8 lg:grid-cols-[minmax(0,1.5fr),320px]">
          <div className="space-y-5 rounded-[28px] border border-slate-800 bg-slate-950/55 p-6 transition-all duration-300">
            {currentStep.content}
            {currentStep.action}
          </div>

          <div className="space-y-4 rounded-[28px] border border-slate-800 bg-slate-950/70 p-6">
            <div className="flex items-center gap-3 text-slate-200">
              <Sparkles className="h-5 w-5 text-amber-300" />
              <p className="font-medium">What happens next</p>
            </div>
            <div className="space-y-3 text-sm text-slate-400">
              <p>Invite operators, enroll devices, and tune policies before broad deployment.</p>
              <p>Use the dashboard checklist afterward to finish any remaining setup work.</p>
              <p>Every step is skippable so you can move at your own pace.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-800 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <Button
            className="border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
            disabled={stepIndex === 0}
            onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
            type="button"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          {stepIndex === steps.length - 1 ? (
            <Button
              className="bg-emerald-500 text-slate-950 hover:bg-emerald-400"
              onClick={completeOnboarding}
              type="button"
            >
              Go to Dashboard
            </Button>
          ) : (
            <Button
              className="bg-blue-600 text-white hover:bg-blue-500"
              onClick={() => setStepIndex((current) => Math.min(steps.length - 1, current + 1))}
              type="button"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
